import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../utils/api';

// ─── Auth Store ───────────────────────────────────────────────────────────────
export const useAuthStore = create(
  persist(
    (set, get) => ({
      user: null,
      token: localStorage.getItem('token') || null,
      isAuthenticated: !!localStorage.getItem('token'),

      login: async (email, password) => {
        const { data } = await api.post('/auth/login', { email, password });
        localStorage.setItem('token', data.token);
        if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
        set({ user: data.user, token: data.token, isAuthenticated: true });
        return data;
      },

      register: async (form) => {
        const { data } = await api.post('/auth/register', form);
        localStorage.setItem('token', data.token);
        set({ user: data.user, token: data.token, isAuthenticated: true });
        return data;
      },

      logout: () => {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        set({ user: null, token: null, isAuthenticated: false });
      },

      fetchMe: async () => {
        try {
          const { data } = await api.get('/auth/me');
          set({ user: data.user, isAuthenticated: true });
        } catch {
          get().logout();
        }
      },
    }),
    { name: 'auth-storage', partialize: (s) => ({ user: s.user, token: s.token, isAuthenticated: s.isAuthenticated }) }
  )
);

// ─── Cart Store ───────────────────────────────────────────────────────────────
export const useCartStore = create(
  persist(
    (set, get) => ({
      items: [],
      loading: false,

      addItem: async (product, quantity = 1, variant = null) => {
        const { isAuthenticated } = useAuthStore.getState();
        if (isAuthenticated) {
          try {
            const { data } = await api.post('/cart/add', { productId: product.id, quantity, variant });
            set({ items: data.cart.items });
            return;
          } catch {}
        }
        // Local fallback
        const key = `${product.id}-${variant || 'default'}`;
        const items = [...get().items];
        const idx = items.findIndex(i => i.key === key);
        if (idx > -1) {
          items[idx] = { ...items[idx], quantity: items[idx].quantity + quantity };
        } else {
          items.push({ key, productId: product.id, name: product.name, price: product.salePrice || product.price, image: product.images?.[0] || '', variant, quantity });
        }
        set({ items });
      },

      updateItem: async (key, quantity) => {
        const { isAuthenticated } = useAuthStore.getState();
        if (isAuthenticated) {
          try {
            const { data } = await api.put('/cart/update', { key, quantity });
            set({ items: data.cart.items });
            return;
          } catch {}
        }
        const items = quantity <= 0
          ? get().items.filter(i => i.key !== key)
          : get().items.map(i => i.key === key ? { ...i, quantity } : i);
        set({ items });
      },

      removeItem: async (key) => {
        const { isAuthenticated } = useAuthStore.getState();
        if (isAuthenticated) {
          try {
            const { data } = await api.delete(`/cart/remove/${key}`);
            set({ items: data.cart.items });
            return;
          } catch {}
        }
        set({ items: get().items.filter(i => i.key !== key) });
      },

      clearCart: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (isAuthenticated) { try { await api.delete('/cart/clear'); } catch {} }
        set({ items: [] });
      },

      fetchCart: async () => {
        const { isAuthenticated } = useAuthStore.getState();
        if (!isAuthenticated) return;
        try {
          set({ loading: true });
          const { data } = await api.get('/cart');
          set({ items: data.cart.items, loading: false });
        } catch { set({ loading: false }); }
      },

      get total() {
        return get().items.reduce((sum, i) => sum + i.price * i.quantity, 0);
      },
      get count() {
        return get().items.reduce((sum, i) => sum + i.quantity, 0);
      },
    }),
    { name: 'cart-storage' }
  )
);

// ─── Wishlist Store ───────────────────────────────────────────────────────────
export const useWishlistStore = create(
  persist(
    (set, get) => ({
      items: [], // array of product objects

      toggle: async (product) => {
        const { isAuthenticated } = useAuthStore.getState();
        const exists = get().items.some(i => i.id === product.id);

        if (isAuthenticated) {
          try {
            await api.post('/wishlist/toggle', { productId: product.id });
          } catch {}
        }

        if (exists) {
          set({ items: get().items.filter(i => i.id !== product.id) });
        } else {
          set({ items: [...get().items, product] });
        }
        return !exists;
      },

      isInWishlist: (productId) => get().items.some(i => i.id === productId),
    }),
    { name: 'wishlist-storage' }
  )
);

// ─── Compare Store ────────────────────────────────────────────────────────────
export const useCompareStore = create(
  persist(
    (set, get) => ({
      items: [],

      toggle: (product) => {
        const exists = get().items.some(i => i.id === product.id);
        if (exists) {
          set({ items: get().items.filter(i => i.id !== product.id) });
        } else {
          if (get().items.length >= 4) return false;
          set({ items: [...get().items, product] });
        }
        return true;
      },

      remove: (productId) => set({ items: get().items.filter(i => i.id !== productId) }),
      clear: () => set({ items: [] }),
      isInCompare: (productId) => get().items.some(i => i.id === productId),
    }),
    { name: 'compare-storage' }
  )
);
